import type {
  GovernedCapabilityIntent,
  GovernedCapabilityIntentKind,
} from './ask-helix/contracts/governed-capability-manifest';
import type {
  DecisionAgentModuleManifest,
  GovernedToolContract,
} from './decision-agent-platform';

/**
 * ONE CONTRACT PER DECLARED INTENT, derived from the module's own declarations.
 *
 * WHY THIS EXISTS. The manager's executable-catalog rule
 * (`runtimeExecutableCatalog`) admits an intent only when EXACTLY ONE governed
 * tool contract lists it and the registered tool for that contract carries a
 * `governedOutputAdapter`. Twenty-eight modules ship the same five-tool
 * lifecycle scaffold (`read_state` / `evaluate_monitoring` / `run_scenarios` /
 * `build_decision_pack` / `approve_activate`) with `intentIds: undefined`, so
 * every intent they declare fails that rule before evidence is even consulted.
 *
 * The SG&A and finance lanes each closed that gap by hand-writing a
 * per-module `*.lifecycle-tools.ts`. Three modules in, the hand-written part is
 * the same in all three: clone the module's own scaffold contract for the
 * runtime purpose that serves this intent's declared `kind`, name it after the
 * intent, and list that one intent. This function is that step, once.
 *
 * WHAT IT DELIBERATELY DOES NOT DO.
 *
 * - It never invents governance posture. Permissions, sensitivity, audit level,
 *   approval policy, side-effect class, rollback posture, idempotency and
 *   human-approval flags are COPIED from the module's own scaffold contract for
 *   that runtime purpose. A module with no scaffold contract for the purpose an
 *   intent kind needs derives no contract for it and is told which purpose is
 *   missing, rather than being handed a default that nobody approved.
 * - It never supplies the `governedOutputAdapter`. That adapter has to read the
 *   module's own payload shape and is irreducibly per-module work; a module
 *   that derives contracts without wiring one gains nothing, which is the
 *   correct outcome — the catalog rule still refuses it.
 * - It is OPT-IN. A manifest is transformed only when its own module calls
 *   `bindDeclaredIntentsToLifecycleTools`; no existing module changes shape
 *   because this file exists.
 * - It branches on the DECLARED `kind` enum, never on an intent id, a tool
 *   name shape, or any question text.
 */

/** The lifecycle purposes a derived contract may claim. */
export type DecisionAgentIntentRuntimePurpose = NonNullable<
  GovernedToolContract['runtimePurpose']
>;

/**
 * The governance posture fields a derived contract inherits verbatim from the
 * module's own scaffold contract. Named so the copy is a spread of one typed
 * shape rather than nine hand-listed fields that can silently drift.
 */
type InheritedToolPosture = Omit<
  GovernedToolContract,
  'toolName' | 'description' | 'intentIds' | 'runtimePurpose'
>;

export type DeclaredIntentToolBindingInput = Readonly<{
  /** The module whose declarations are the only input to the derivation. */
  manifest: DecisionAgentModuleManifest;
  /**
   * The tool-name namespace the module already uses, e.g.
   * `org_analysis_optimization`. Derived names are
   * `<namespace>.<intentId>.<runtimePurpose>`.
   */
  toolNamespace: string;
  /**
   * Which lifecycle purpose serves each declared intent kind. A closed record
   * over the kinds this module actually declares; an intent whose kind is
   * absent from it is left unbound (and reported), never guessed.
   */
  runtimePurposeByIntentKind: Readonly<
    Partial<Record<GovernedCapabilityIntentKind, DecisionAgentIntentRuntimePurpose>>
  >;
  /**
   * Optional per-intent override of the kind map, for the case where an intent's
   * declared KIND and the lifecycle purpose that actually serves it today differ.
   * A scenario-kind intent whose producer is the module's deterministic
   * monitoring path is the real instance: routing it to `run_scenarios` would
   * reach a producer that does not exist yet, and declaring its kind as `read`
   * to dodge that would be a lie about the intent. The override states the true
   * pairing, and the module must say why in its own binding file.
   */
  runtimePurposeByIntentId?: Readonly<
    Record<string, DecisionAgentIntentRuntimePurpose>
  >;
  /**
   * Optional opt-in subset. Absent means every declared intent whose kind maps
   * to a purpose. Present means only these ids are considered, so a module can
   * land its read intents before its scenario intents have a producer.
   */
  intentIds?: readonly string[];
}>;

export type DeclaredIntentToolBindingResult = Readonly<{
  /** One contract per bound intent, in declaration order. */
  derivedTools: readonly GovernedToolContract[];
  /** Intent ids that got a contract, in declaration order. */
  boundIntentIds: readonly string[];
  /**
   * Intent ids deliberately left unbound, each with the reason. Reported
   * rather than silently dropped: an intent the manager will not offer is a
   * fact the module's own conformance spec should be able to assert.
   */
  unboundIntents: readonly Readonly<{
    intentId: string;
    reason:
      | 'intent_kind_has_no_declared_runtime_purpose'
      | 'module_declares_no_scaffold_tool_for_runtime_purpose'
      | 'not_requested';
  }>[];
}>;

/** The module's own scaffold contract for a purpose, or null. */
function scaffoldToolForPurpose(
  manifest: DecisionAgentModuleManifest,
  runtimePurpose: DecisionAgentIntentRuntimePurpose,
): GovernedToolContract | null {
  const owners = manifest.governedTools.filter(
    (tool) => tool.runtimePurpose === runtimePurpose,
  );
  return owners.length === 1 ? owners[0] : null;
}

function inheritedPosture(scaffold: GovernedToolContract): InheritedToolPosture {
  const {
    toolName: _toolName,
    description: _description,
    intentIds: _intentIds,
    runtimePurpose: _runtimePurpose,
    ...posture
  } = scaffold;
  return posture;
}

function derivedToolFor(input: {
  intent: GovernedCapabilityIntent;
  scaffold: GovernedToolContract;
  runtimePurpose: DecisionAgentIntentRuntimePurpose;
  toolNamespace: string;
}): GovernedToolContract {
  return {
    ...inheritedPosture(input.scaffold),
    toolName: `${input.toolNamespace}.${input.intent.intentId}.${input.runtimePurpose}`,
    description: input.intent.description,
    runtimePurpose: input.runtimePurpose,
    intentIds: [input.intent.intentId],
  };
}

/**
 * Derive one lifecycle contract per declared intent. Pure: it reads the
 * manifest and returns contracts; it mutates nothing.
 */
export function deriveIntentBoundGovernedTools(
  input: DeclaredIntentToolBindingInput,
): DeclaredIntentToolBindingResult {
  const requested = input.intentIds ? new Set(input.intentIds) : null;
  const derivedTools: GovernedToolContract[] = [];
  const boundIntentIds: string[] = [];
  const unboundIntents: DeclaredIntentToolBindingResult['unboundIntents'][number][] =
    [];

  for (const intent of input.manifest.governedCapability?.intents ?? []) {
    if (requested && !requested.has(intent.intentId)) {
      unboundIntents.push({ intentId: intent.intentId, reason: 'not_requested' });
      continue;
    }
    const runtimePurpose =
      input.runtimePurposeByIntentId?.[intent.intentId] ??
      input.runtimePurposeByIntentKind[intent.kind];
    if (!runtimePurpose) {
      unboundIntents.push({
        intentId: intent.intentId,
        reason: 'intent_kind_has_no_declared_runtime_purpose',
      });
      continue;
    }
    const scaffold = scaffoldToolForPurpose(input.manifest, runtimePurpose);
    if (!scaffold) {
      unboundIntents.push({
        intentId: intent.intentId,
        reason: 'module_declares_no_scaffold_tool_for_runtime_purpose',
      });
      continue;
    }
    derivedTools.push(
      derivedToolFor({
        intent,
        scaffold,
        runtimePurpose,
        toolNamespace: input.toolNamespace,
      }),
    );
    boundIntentIds.push(intent.intentId);
  }

  return { derivedTools, boundIntentIds, unboundIntents };
}

/**
 * The manifest with its derived per-intent contracts APPENDED to the scaffold.
 *
 * Appended, never replacing: the five scaffold contracts stay exactly as they
 * are, because existing callers dispatch on them and the module's own
 * `scenarioRunner` / `monitoringRuleEvaluator` name them. The scaffold
 * contracts keep `intentIds: undefined`, so exactly one contract lists any
 * bound intent and the manager's catalog rule is satisfied.
 */
export function bindDeclaredIntentsToLifecycleTools(
  input: DeclaredIntentToolBindingInput,
): DecisionAgentModuleManifest {
  const { derivedTools } = deriveIntentBoundGovernedTools(input);
  if (derivedTools.length === 0) return input.manifest;
  return {
    ...input.manifest,
    governedTools: [...input.manifest.governedTools, ...derivedTools],
  };
}
